import { useAppStore } from '@store/appStore';

/**
 * Playback Speed Button Component
 */
interface SpeedButtonProps {
  speed: number;
  currentSpeed: number;
  onClick: (speed: number) => void;
}

function SpeedButton({ speed, currentSpeed, onClick }: SpeedButtonProps) {
  const isActive = currentSpeed === speed;

  return (
    <button
      onClick={() => onClick(speed)}
      className={`speed-button ${isActive ? 'active' : ''}`}
      aria-label={`Set playback speed to ${speed}x`}
    >
      {speed}x
    </button>
  );
}

/**
 * Playback Mode Button Component
 */
interface ModeButtonProps {
  mode: 'sequence' | 'random';
  currentMode: 'sequence' | 'random' | null;
  onClick: (mode: 'sequence' | 'random') => void;
}

function ModeButton({ mode, currentMode, onClick }: ModeButtonProps) {
  const isActive = currentMode === mode;
  const label = mode === 'sequence' ? 'Sequential' : 'Random';

  return (
    <button
      onClick={() => onClick(mode)}
      className={`mode-button ${isActive ? 'active' : ''}`}
      aria-label={`Set playback mode to ${label.toLowerCase()}`}
    >
      {label}
    </button>
  );
}

/**
 * Playback Panel Component
 */
export function PlaybackPanel() {
  const playback = useAppStore(state => state.playback);
  const startPlayback = useAppStore(state => state.startPlayback);
  const stopPlayback = useAppStore(state => state.stopPlayback);
  const setPlaybackSpeed = useAppStore(state => state.setPlaybackSpeed);

  // Debug log
  console.log('PlaybackPanel rendering with:', { 
    isPlaying: playback.isPlaying,
    mode: playback.mode,
    speed: playback.speed 
  });

  const handlePlay = () => {
    if (!playback.mode) {
      // Default to sequence mode if no mode selected
      startPlayback('sequence');
    } else if (!playback.isPlaying) {
      startPlayback(playback.mode);
    }
  };

  const handlePause = () => {
    if (playback.isPlaying) {
      stopPlayback();
    }
  };

  const handleSpeedChange = (speed: number) => {
    setPlaybackSpeed(speed);
  };

  const handleModeChange = (mode: 'sequence' | 'random') => {
    if (playback.isPlaying) {
      // If currently playing, restart with new mode
      stopPlayback();
      setTimeout(() => startPlayback(mode), 100); // Small delay to ensure stop completes
    } else {
      // Just set the mode for next play
      // Note: This is a bit tricky since startPlayback sets the mode
      // For now, we'll just start playback with the new mode
      startPlayback(mode);
    }
  };

  const playIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M8 5v14l11-7z"/>
    </svg>
  );

  const pauseIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
    </svg>
  );

  return (
    <div className="playback-panel">
      <div className="panel-header">
        <h3>Temporal Playback</h3>
        <div className="playback-status">
          {playback.isPlaying ? (
            <span className="playing">Playing ({playback.speed}x)</span>
          ) : (
            <span className="stopped">Stopped</span>
          )}
        </div>
      </div>

      <div className="control-group">
        <div className="play-controls">
          {!playback.isPlaying ? (
            <button
              onClick={handlePlay}
              className="play-button"
              aria-label="Start playback"
            >
              {playIcon}
              Play
            </button>
          ) : (
            <button
              onClick={handlePause}
              className="pause-button"
              aria-label="Pause playback"
            >
              {pauseIcon}
              Pause
            </button>
          )}
        </div>

        <div className="mode-controls">
          <label>Mode:</label>
          <div className="mode-buttons">
            <ModeButton
              mode="sequence"
              currentMode={playback.mode}
              onClick={handleModeChange}
            />
            <ModeButton
              mode="random"
              currentMode={playback.mode}
              onClick={handleModeChange}
            />
          </div>
        </div>
      </div>

      <div className="speed-controls">
        <label>Speed:</label>
        <div className="speed-buttons">
          <SpeedButton speed={0.5} currentSpeed={playback.speed} onClick={handleSpeedChange} />
          <SpeedButton speed={1} currentSpeed={playback.speed} onClick={handleSpeedChange} />
          <SpeedButton speed={2} currentSpeed={playback.speed} onClick={handleSpeedChange} />
          <SpeedButton speed={4} currentSpeed={playback.speed} onClick={handleSpeedChange} />
        </div>
      </div>
    </div>
  );
}